import { useState, useEffect } from "react";
import { enqueueSnackbar } from "notistack";
import { Plus, Clock, X, Play, Pause, StopCircle, ArrowRight, Shield, Eye, Users, CheckCircle, XCircle, Ban, UserPlus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { SelectSingle } from "../../components/SelectSingle";
import { InputText } from "../../components/InputText";
import { CriarPatrulhaModal } from "../../components/modals/CriarPatrulhaModal";
import { MilitarSelectField } from "../../components/MilitarSelectField";
import { SessaoCard } from "../../components/SessaoCard";
import { patrulhasService, Patrulha } from "../../services/patrulhas";
import { patrulhasResponsaveisService, PatrulhaResponsavel } from "../../services/patrulhasResponsaveis";

import { useAuth } from "../../contexts/AuthContext";
import moment from "moment";
import { SkeletonResponsavel, SkeletonPatrulhaCard, StaggerWrapper, FadeInWrapper } from "../../components/Skeleton";

export function Patrulhas() {
  const { militar } = useAuth();
  const [patrulhas, setPatrulhas] = useState<Patrulha[]>([]);
  const [responsaveis, setResponsaveis] = useState<PatrulhaResponsavel[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingResponsaveis, setLoadingResponsaveis] = useState(true);
  const [loadingPatrulhas, setLoadingPatrulhas] = useState(true);
  const [modalCriarAberta, setModalCriarAberta] = useState(false);
  const [modalEditarAberta, setModalEditarAberta] = useState(false);
  const [patrulhaEditando, setPatrulhaEditando] = useState<Patrulha | null>(null);
  const [modalPassarAberta, setModalPassarAberta] = useState(false);

  // Modais de confirmação de status
  const [modalConcluidaAberta, setModalConcluidaAberta] = useState(false);
  const [modalIncompletaAberta, setModalIncompletaAberta] = useState(false);
  const [modalCanceladaAberta, setModalCanceladaAberta] = useState(false);
  const [patrulhaSelecionada, setPatrulhaSelecionada] = useState<Patrulha | null>(null);

  // Modal de confirmação finalizar função
  const [modalFinalizarFuncaoAberta, setModalFinalizarFuncaoAberta] = useState(false);

  // Estados para passar função
  const [idMilitarNovo, setIdMilitarNovo] = useState("");

  // Estados para editar status
  const [novoStatus, setNovoStatus] = useState("");
  const [novoTrouxe, setNovoTrouxe] = useState("");



  const statusOptions = [
    { value: "EA", label: "Em Andamento" },
    { value: "CO", label: "Concluída" },
    { value: "IN", label: "Incompleta" },
    { value: "CA", label: "Cancelada" },
  ];

  useEffect(() => {
    loadPatrulhas();
    loadResponsaveis();

    // Atualização automática a cada 20 segundos
    const autoUpdateInterval = setInterval(() => {
      loadPatrulhas();
      loadResponsaveis();
    }, 20000);

    return () => clearInterval(autoUpdateInterval);
  }, []);

  useEffect(() => {
    if (modalPassarAberta) {
      setIdMilitarNovo("");
    }
  }, [modalPassarAberta]);

  const loadPatrulhas = async () => {
    try {
      setLoadingPatrulhas(true);
      const data = await patrulhasService.list();
      setPatrulhas(data);
    } catch (error: any) {
      console.error("Erro ao carregar patrulhas:", error);
      enqueueSnackbar(error.response?.data?.message || "Erro ao carregar patrulhas", {
        variant: "error",
      });
    } finally {
      setLoadingPatrulhas(false);
    }
  };

  const loadResponsaveis = async () => {
    try {
      setLoadingResponsaveis(true);
      const data = await patrulhasResponsaveisService.listarAtivos();
      setResponsaveis(data);
    } catch (error: any) {
      console.error("Erro ao carregar responsáveis:", error);
      enqueueSnackbar(error.response?.data?.message || "Erro ao carregar responsáveis", {
        variant: "error",
      });
    } finally {
      setLoadingResponsaveis(false);
    }
  };

  const getPatrulhasDoDia = () => {
    const agora = moment();
    let inicioDia;

    if (agora.hour() < 4) {
      // Antes das 4h da manhã - conta desde as 4h de ontem
      inicioDia = moment().subtract(1, 'day').hour(4).minute(0).second(0);
    } else {
      // Depois das 4h - conta desde as 4h de hoje
      inicioDia = moment().hour(4).minute(0).second(0);
    }

    return patrulhas.filter((patrulha) => {
      const inicioPatrulha = moment(patrulha.inicio);
      return inicioPatrulha.isAfter(inicioDia) && inicioPatrulha.isBefore(agora);
    });
  };



  const handleAbrirEditar = (patrulha: Patrulha) => {
    setPatrulhaEditando(patrulha);
    setNovoStatus(patrulha.status);
    setNovoTrouxe(patrulha.trouxe || "");
    setModalEditarAberta(true);
  };

  const handleSalvarStatus = async () => {
    if (!patrulhaEditando || !novoStatus) {
      enqueueSnackbar("Selecione um status", { variant: "error" });
      return;
    }

    try {
      setLoading(true);
      await patrulhasService.updateStatus(patrulhaEditando.id, {
        status: novoStatus,
        trouxe: novoTrouxe,
        termino: novoStatus !== "EA" ? moment().toISOString() : undefined,
      });

      enqueueSnackbar("Status atualizado com sucesso!", { variant: "success" });
      setModalEditarAberta(false);
      limparFormularioEditar();
      loadPatrulhas();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao atualizar status", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const limparFormularioEditar = () => {
    setPatrulhaEditando(null);
    setNovoStatus("");
    setNovoTrouxe("");
  };

  // Novas funções para os ícones de status
  const handleAbrirModalConcluida = (patrulha: Patrulha) => {
    setPatrulhaSelecionada(patrulha);
    setNovoTrouxe("");
    setModalConcluidaAberta(true);
  };

  const handleAbrirModalIncompleta = (patrulha: Patrulha) => {
    setPatrulhaSelecionada(patrulha);
    setModalIncompletaAberta(true);
  };

  const handleAbrirModalCancelada = (patrulha: Patrulha) => {
    setPatrulhaSelecionada(patrulha);
    setModalCanceladaAberta(true);
  };

  const handleConfirmarConcluida = async () => {
    if (!patrulhaSelecionada || !novoTrouxe.trim()) {
      enqueueSnackbar("Informe o nick de quem trouxe", { variant: "error" });
      return;
    }

    try {
      setLoading(true);
      await patrulhasService.updateStatus(patrulhaSelecionada.id, {
        status: "CO",
        trouxe: novoTrouxe,
        termino: moment().toISOString(),
      });

      enqueueSnackbar("Patrulha marcada como concluída!", { variant: "success" });
      setModalConcluidaAberta(false);
      setPatrulhaSelecionada(null);
      setNovoTrouxe("");
      loadPatrulhas();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao atualizar status", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarIncompleta = async () => {
    if (!patrulhaSelecionada) return;

    try {
      setLoading(true);
      await patrulhasService.updateStatus(patrulhaSelecionada.id, {
        status: "IN",
        termino: moment().toISOString(),
      });

      enqueueSnackbar("Patrulha marcada como incompleta", { variant: "success" });
      setModalIncompletaAberta(false);
      setPatrulhaSelecionada(null);
      loadPatrulhas();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao atualizar status", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarCancelada = async () => {
    if (!patrulhaSelecionada) return;

    try {
      setLoading(true);
      await patrulhasService.updateStatus(patrulhaSelecionada.id, {
        status: "CA",
        termino: moment().toISOString(),
      });

      enqueueSnackbar("Patrulha cancelada", { variant: "success" });
      setModalCanceladaAberta(false);
      setPatrulhaSelecionada(null);
      loadPatrulhas();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao atualizar status", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Funções de gerenciamento de responsáveis
  const handleAssumirFuncao = async (funcao: string) => {
    try {
      setLoading(true);
      await patrulhasResponsaveisService.assumir({
        funcao,
      });
      enqueueSnackbar(`Você assumiu a função de ${funcao}`, { variant: "success" });
      loadResponsaveis();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao assumir função", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePegarFuncao = async (funcao: string) => {
    try {
      setLoading(true);
      await patrulhasResponsaveisService.pegar({
        funcao,
      });
      enqueueSnackbar(`Você pegou a função de ${funcao}`, { variant: "success" });
      loadResponsaveis();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao pegar função", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFinalizar = async () => {
    try {
      setLoading(true);
      await patrulhasResponsaveisService.finalizar();
      enqueueSnackbar("Função finalizada", { variant: "success" });
      loadResponsaveis();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao finalizar", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePassar = async () => {
    if (!idMilitarNovo) {
      enqueueSnackbar("Selecione um militar", { variant: "error" });
      return;
    }

    try {
      setLoading(true);
      await patrulhasResponsaveisService.passar({ id_militar_novo: idMilitarNovo });
      enqueueSnackbar("Função passada", { variant: "success" });
      setModalPassarAberta(false);
      setIdMilitarNovo("");
      loadResponsaveis();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao passar função", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "EA":
        return <Badge variant="default">Em Andamento</Badge>;
      case "CO":
        return <Badge className="bg-blue-100 text-blue-800">Concluída</Badge>;
      case "IN":
        return <Badge className="bg-yellow-100 text-yellow-800">Incompleta</Badge>;
      case "CA":
        return <Badge variant="outline">Cancelada</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const calcularTempo = (inicio: string) => {
    const inicioMoment = moment(inicio);
    const agora = moment();
    const duration = moment.duration(agora.diff(inicioMoment));

    const horas = Math.floor(duration.asHours()).toString().padStart(2, "0");
    const minutos = duration.minutes().toString().padStart(2, "0");
    const segundos = duration.seconds().toString().padStart(2, "0");

    return `${horas}:${minutos}:${segundos}`;
  };

  const calcularTempoResponsavel = (responsavel: PatrulhaResponsavel) => {
    const inicio = moment(responsavel.data_hora_inicio);
    const agora = moment();
    const tempoTotalMs = agora.diff(inicio);
    const tempoPausadoMs = responsavel.tempo_pausado_segundos * 1000;
    const tempoAtivoMs = tempoTotalMs - tempoPausadoMs;

    const duration = moment.duration(tempoAtivoMs);
    const horas = Math.floor(duration.asHours()).toString().padStart(2, "0");
    const minutos = duration.minutes().toString().padStart(2, "0");
    const segundos = duration.seconds().toString().padStart(2, "0");

    return `${horas}:${minutos}:${segundos}`;
  };

  // Contador de tempo em tempo real
  const [tempos, setTempos] = useState<Record<string, string>>({});
  const [temposResponsaveis, setTemposResponsaveis] = useState<Record<string, string>>({});

  useEffect(() => {
    const interval = setInterval(() => {
      const novosTempos: Record<string, string> = {};
      patrulhasEmAndamento.forEach((patrulha) => {
        novosTempos[patrulha.id] = calcularTempo(patrulha.inicio);
      });
      setTempos(novosTempos);

      const novosTemposResp: Record<string, string> = {};
      responsaveis.forEach((resp) => {
        if (resp.status !== "finalizado") {
          novosTemposResp[resp.id] = calcularTempoResponsavel(resp);
        }
      });
      setTemposResponsaveis(novosTemposResp);
    }, 1000);

    return () => clearInterval(interval);
  }, [patrulhas, responsaveis]);

  const patrulhasEmAndamento = patrulhas.filter((p) => p.status === "EA");
  const patrulhasDoDia = getPatrulhasDoDia();

  const meuResponsavel = responsaveis.find((r) => r.id_militar === militar?.id);
  const supervisor = responsaveis.find((r) => r.funcao === "supervisor");
  const monitor = responsaveis.find((r) => r.funcao === "monitor");
  const auxiliar = responsaveis.find((r) => r.funcao === "auxiliar");



  const FuncaoCard = ({ titulo, icone: Icone, funcao, responsavel }: any) => {
    const souEu = responsavel?.id_militar === militar?.id;
    const temAlguem = !!responsavel;

    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Icone className="h-5 w-5" />
            {titulo}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {temAlguem ? (
            <>
              <div className="flex flex-col items-center gap-3">
                <img
                  src={`https://www.habbo.com.br/habbo-imaging/avatarimage?&user=${responsavel.militar.nick}&action=std&direction=2&head_direction=2&img_format=png&gesture=std&headonly=0&size=l`}
                  alt={responsavel.militar.nick}
                  className="w-24 h-24 object-cover"
                  style={{ imageRendering: 'pixelated' }}
                />
                <div className="text-center w-full">
                  <span className="font-medium">{responsavel.militar.nick}</span>
                  {temposResponsaveis[responsavel.id] && (
                    <p className="text-sm font-mono text-blue-600 dark:text-blue-400 flex items-center justify-center gap-1 mt-1">
                      <Clock className="h-3 w-3" />
                      {temposResponsaveis[responsavel.id]}
                    </p>
                  )}
                </div>
              </div>
              {souEu ? (
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => setModalFinalizarFuncaoAberta(true)} className="flex-1">
                    <StopCircle className="h-3 w-3 mr-1" />
                    Finalizar
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setModalPassarAberta(true)} className="flex-1">
                    <ArrowRight className="h-3 w-3 mr-1" />
                    Passar
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  className="w-full"
                  onClick={() => handlePegarFuncao(funcao)}
                  disabled={loading || !!meuResponsavel}
                >
                  <UserPlus className="h-3 w-3 mr-1" />
                  Pegar {titulo}
                </Button>
              )}
            </>
          ) : (
            <Button
              size="sm"
              className="w-full"
              onClick={() => handleAssumirFuncao(funcao)}
              disabled={loading || !!meuResponsavel}
            >
              Assumir {titulo}
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      {/* Card de Sessão de Patrulha */}
      <SessaoCard onSessaoChange={loadResponsaveis} />

      {/* Cards de Responsáveis */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {loadingResponsaveis ? (
          <>
            <SkeletonResponsavel />
            <SkeletonResponsavel />
            <SkeletonResponsavel />
          </>
        ) : (
          <>
            <FadeInWrapper delay={0}>
              <FuncaoCard
                titulo="Supervisor"
                icone={Shield}
                funcao="supervisor"
                responsavel={supervisor}
              />
            </FadeInWrapper>
            <FadeInWrapper delay={0.1}>
              <FuncaoCard
                titulo="Monitor"
                icone={Eye}
                funcao="monitor"
                responsavel={monitor}
              />
            </FadeInWrapper>
            <FadeInWrapper delay={0.2}>
              <FuncaoCard
                titulo="Auxiliar"
                icone={Users}
                funcao="auxiliar"
                responsavel={auxiliar}
              />
            </FadeInWrapper>
          </>
        )}
      </div>

      {/* Modal Passar Função */}
      <Dialog open={modalPassarAberta} onOpenChange={setModalPassarAberta}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Passar Função</DialogTitle>
            <DialogDescription>
              Selecione o militar que receberá a função de oficial da patrulha.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Militar</Label>
              <div className="mt-1">
                <MilitarSelectField
                  placeholder="Digite o nick do militar..."
                  onChange={(militarId) => setIdMilitarNovo(militarId)}
                />
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setModalPassarAberta(false)}>
                Cancelar
              </Button>
              <Button onClick={handlePassar} disabled={loading || !idMilitarNovo}>
                Passar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Patrulhas em Andamento
              </CardTitle>
              <Badge variant="secondary" className="text-sm px-3 py-1">
                {patrulhasDoDia.length} patrulha{patrulhasDoDia.length !== 1 ? 's' : ''} no dia
              </Badge>
            </div>
            <>
              <Button onClick={() => setModalCriarAberta(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Patrulha
              </Button>
              <CriarPatrulhaModal
                isOpen={modalCriarAberta}
                onClose={() => setModalCriarAberta(false)}
                onSuccess={loadPatrulhas}
              />
            </>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {loadingPatrulhas ? (
              <>
                <SkeletonPatrulhaCard />
                <SkeletonPatrulhaCard />
                <SkeletonPatrulhaCard />
              </>
            ) : patrulhasEmAndamento.length === 0 ? (
              <p className="col-span-full text-center text-muted-foreground py-8">
                Nenhuma patrulha em andamento
              </p>
            ) : (
              patrulhasEmAndamento.map((patrulha, index) => (
                <StaggerWrapper key={patrulha.id} index={index}>
                  <Card className="bg-gray-50 dark:bg-gray-800/50">
                    <CardContent className="p-3">
                      <div className="flex flex-col items-center gap-2">
                        {/* Habbo Imager andando */}
                        <img
                          src={`https://www.habbo.com.br/habbo-imaging/avatarimage?&user=${patrulha.patrulheiro?.nick}&action=wlk&direction=2&head_direction=2&img_format=png&gesture=std&headonly=0&size=b`}
                          alt={patrulha.patrulheiro?.nick}
                          className="w-16 h-20 object-cover"
                          style={{ imageRendering: 'pixelated' }}
                        />

                        {/* Informações da patrulha */}
                        <div className="text-center w-full space-y-0.5">
                          <p className="font-medium text-xs text-gray-900 dark:text-gray-100">
                            {patrulha.patrulheiro?.patente?.abreviacao} {patrulha.patrulheiro?.nick}
                          </p>
                          <p className="text-[10px] text-gray-600 dark:text-gray-400">
                            Oficial: {patrulha.oficial?.patente?.abreviacao} {patrulha.oficial?.nick}
                          </p>
                          {tempos[patrulha.id] && (
                            <p className="text-[10px] font-mono text-blue-600 dark:text-blue-400 flex items-center justify-center gap-1">
                              <Clock className="h-2.5 w-2.5" />
                              {tempos[patrulha.id]}
                            </p>
                          )}
                        </div>

                        {/* Ícones de ação de status */}
                        <div className="flex gap-0.5 justify-center pt-1.5 border-t w-full">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAbrirModalConcluida(patrulha)}
                            className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                            title="Marcar como Concluída"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAbrirModalIncompleta(patrulha)}
                            className="h-8 w-8 p-0 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50 dark:hover:bg-yellow-900/20"
                            title="Marcar como Incompleta"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAbrirModalCancelada(patrulha)}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                            title="Cancelar Patrulha"
                          >
                            <Ban className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </StaggerWrapper>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Modal Concluída */}
      <Dialog open={modalConcluidaAberta} onOpenChange={setModalConcluidaAberta}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Marcar Patrulha como Concluída</DialogTitle>
            <DialogDescription>
              Informe o nick de quem foi trazido na patrulha.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {patrulhaSelecionada && (
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm font-medium">
                  Oficial: {patrulhaSelecionada.oficial?.patente?.abreviacao} {patrulhaSelecionada.oficial?.nick}
                </p>
                <p className="text-sm text-muted-foreground">
                  Patrulheiro: {patrulhaSelecionada.patrulheiro?.patente?.abreviacao} {patrulhaSelecionada.patrulheiro?.nick}
                </p>
              </div>
            )}

            <InputText
              title="Nick de quem trouxe"
              value={novoTrouxe}
              onChange={(e) => setNovoTrouxe(e.target.value)}
              placeholder="Digite o nick de quem foi trazido"
              required
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setModalConcluidaAberta(false);
                setPatrulhaSelecionada(null);
                setNovoTrouxe("");
              }}
            >
              Cancelar
            </Button>
            <Button onClick={handleConfirmarConcluida} disabled={loading}>
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Incompleta */}
      <Dialog open={modalIncompletaAberta} onOpenChange={setModalIncompletaAberta}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Marcar Patrulha como Incompleta</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja marcar esta patrulha como incompleta?
            </DialogDescription>
          </DialogHeader>
          {patrulhaSelecionada && (
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg my-4">
              <p className="text-sm font-medium">
                Oficial: {patrulhaSelecionada.oficial?.patente?.abreviacao} {patrulhaSelecionada.oficial?.nick}
              </p>
              <p className="text-sm text-muted-foreground">
                Patrulheiro: {patrulhaSelecionada.patrulheiro?.patente?.abreviacao} {patrulhaSelecionada.patrulheiro?.nick}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setModalIncompletaAberta(false);
                setPatrulhaSelecionada(null);
              }}
            >
              Cancelar
            </Button>
            <Button onClick={handleConfirmarIncompleta} disabled={loading} variant="default">
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Cancelada */}
      <Dialog open={modalCanceladaAberta} onOpenChange={setModalCanceladaAberta}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cancelar Patrulha</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja cancelar esta patrulha?
            </DialogDescription>
          </DialogHeader>
          {patrulhaSelecionada && (
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg my-4">
              <p className="text-sm font-medium">
                Oficial: {patrulhaSelecionada.oficial?.patente?.abreviacao} {patrulhaSelecionada.oficial?.nick}
              </p>
              <p className="text-sm text-muted-foreground">
                Patrulheiro: {patrulhaSelecionada.patrulheiro?.patente?.abreviacao} {patrulhaSelecionada.patrulheiro?.nick}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setModalCanceladaAberta(false);
                setPatrulhaSelecionada(null);
              }}
            >
              Cancelar
            </Button>
            <Button onClick={handleConfirmarCancelada} disabled={loading} variant="destructive">
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Alterar Status - Mantido para compatibilidade, mas não será usado */}
      <Dialog open={modalEditarAberta} onOpenChange={setModalEditarAberta}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Alterar Status da Patrulha</DialogTitle>
            <DialogDescription>
              Atualize o status da patrulha e adicione informações relevantes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {patrulhaEditando && (
              <>
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-sm font-medium">
                    Oficial: {patrulhaEditando.oficial?.patente?.abreviacao}{" "}
                    {patrulhaEditando.oficial?.nick}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Patrulheiro: {patrulhaEditando.patrulheiro?.patente?.abreviacao}{" "}
                    {patrulhaEditando.patrulheiro?.nick}
                  </p>
                </div>

                <SelectSingle
                  title="Status"
                  value={novoStatus}
                  onChange={(e) => setNovoStatus(e.target.value)}
                  required
                >
                  <option value="">Selecione um status</option>
                  {statusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </SelectSingle>

                {novoStatus === "CO" && (
                  <InputText
                    title="Nick de quem trouxe"
                    value={novoTrouxe}
                    onChange={(e) => setNovoTrouxe(e.target.value)}
                    placeholder="Digite o nick de quem foi trazido"
                    required
                  />
                )}

                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setModalEditarAberta(false);
                      limparFormularioEditar();
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button onClick={handleSalvarStatus} disabled={loading}>
                    Salvar
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Confirmar Finalizar Função */}
      <Dialog open={modalFinalizarFuncaoAberta} onOpenChange={setModalFinalizarFuncaoAberta}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Finalizar Função</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja finalizar sua função na patrulha?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalFinalizarFuncaoAberta(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={() => { setModalFinalizarFuncaoAberta(false); handleFinalizar(); }} disabled={loading}>
              Finalizar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
